import React, { ReactNode, useState, useEffect } from 'react';
import { VStack, Box, Checkbox } from 'native-base';
import { i18nFn, Plan } from '@springbig/shared';
import { Caption, H2, H3, H4 } from '~/shared/Typography';
import { useGeneralStyles } from '~/shared/styles';
import OurPressable from '~/shared/Pressable';

export type BillingSchedule = 'monthly' | 'yearly';

type SubscribeFormProps = {
    children: ReactNode;
    plan: Plan;
    onUpdateBillingSchedule: (value: BillingSchedule) => void;
    isLoading: boolean;
    t: i18nFn;
};

const SubscribeForm = ({
    children,
    plan,
    onUpdateBillingSchedule,
    isLoading,
    t
}: SubscribeFormProps) => {

    const gStyle = useGeneralStyles();
    const [isMonthly, setIsMonthly] = useState<boolean>(true);
    const [isYearly, setIsYearly] = useState<boolean>(false);

    useEffect(() => {

        onUpdateBillingSchedule(isMonthly ? 'monthly' : 'yearly');
    }, [isMonthly, isYearly, onUpdateBillingSchedule]);

    useEffect(() => {

        if (plan.billing_schedule === 'yearly') {
            setIsMonthly(false);
            setIsYearly(true);
        }
    }, [plan]);

    return (
        <VStack paddingTop={5} paddingBottom={8} space={8} px={4}>
            { plan.billing_schedule === 'both' &&
                <VStack space={2}>
                    <H2 textAlign={'center'}>
                        { t('credit_card.choose_plan') }
                    </H2>

                    <OurPressable onPress={() => {

                        setIsMonthly(true); setIsYearly(false);
                    }}>
                        <Box
                            backgroundColor={gStyle.section_background}
                            padding={2}
                            flexDirection={'row'}
                            justifyContent={'space-between'}
                            alignItems={'flex-start'}
                            borderRadius={5}
                            borderWidth={2}
                            borderColor={isMonthly ? gStyle.button_background : 'transparent'}
                        >
                            <VStack space={2}>
                                <Box>
                                    <H3>{ t('credit_card.monthly_title') }</H3>
                                    <H4 fontSize={20}>
                                        { t('credit_card.monthly_price', [plan.recurring_amount.toFixed(2)]) }
                                    </H4>
                                </Box>
                                { plan.trial_period > 0 &&
                                    <Box
                                        backgroundColor={'hsl(120, 40%, 80%)'}
                                        paddingX={1}
                                        borderRadius={3} w={'fit-content'}
                                    >
                                        <Caption
                                            fontWeight={'700'}
                                            color={'hsl(120, 40%, 40%)'}
                                            fontSize={12}
                                        >
                                            { t('credit_card.free_trial', [plan.trial_period]) }
                                        </Caption>
                                    </Box>
                                }
                                <Caption opacity={.5}>
                                    { t('credit_card.monthly_summary', [plan.recurring_amount.toFixed(2)]) }
                                </Caption>
                            </VStack>
                            <Checkbox
                                flex={1}
                                alignSelf={'flex-start'}
                                isChecked={isMonthly}
                                value={isMonthly.toString()}
                                isDisabled={isLoading}
                                onChange={(value) => {

                                    if (value) {
                                        setIsMonthly(value);
                                        setIsYearly(!value);
                                    }
                                }}
                                _checked={{
                                    value: '',
                                    bg: gStyle.button_background,
                                    _hover: {
                                        bg: gStyle.button_background,
                                    }
                                }}
                            />
                        </Box>
                    </OurPressable>

                    <OurPressable onPress={() => {

                        setIsMonthly(false); setIsYearly(true);
                    }}>
                        <Box
                            backgroundColor={gStyle.section_background}
                            padding={2}
                            flexDirection={'row'}
                            justifyContent={'space-between'}
                            alignItems={'flex-start'}
                            borderRadius={5}
                            borderWidth={2}
                            borderColor={isYearly ? gStyle.button_background : 'transparent'}
                        >
                            <VStack space={2}>
                                <Box>
                                    <H3>{ t('credit_card.yearly_title') }</H3>
                                    <H4 fontSize={20}>
                                        { t('credit_card.yearly_price', [plan.yearly_recurring_amount.toFixed(2)]) }
                                    </H4>
                                </Box>
                                { plan.trial_period > 0 &&
                                    <Box
                                        backgroundColor={'hsl(120, 40%, 80%)'}
                                        paddingX={1}
                                        borderRadius={3} w={'fit-content'}
                                    >
                                        <Caption
                                            fontWeight={'700'}
                                            color={'hsl(120, 40%, 40%)'}
                                            fontSize={12}
                                        >
                                            { t('credit_card.free_trial', [plan.trial_period]) }
                                        </Caption>
                                    </Box>
                                }
                                <Caption opacity={.5}>
                                    { t('credit_card.yearly_summary', [plan.yearly_recurring_amount.toFixed(2)]) }
                                </Caption>
                            </VStack>
                            <Checkbox
                                flex={1}
                                isChecked={isYearly}
                                value={isYearly.toString()}
                                isDisabled={isLoading}
                                onChange={(value) => {

                                    if (value) {
                                        setIsMonthly(!value);
                                        setIsYearly(value);
                                    }
                                }}
                                _checked={{
                                    value: '',
                                    bg: gStyle.button_background,
                                    _hover: {
                                        bg: gStyle.button_background,
                                    }
                                }}
                            />
                        </Box>
                    </OurPressable>
                </VStack>
            }

            <Box>
                <H2 textAlign={'center'}>
                    { t('credit_card.add_card') }
                </H2>

                { children }
            </Box>
        </VStack>
    );
};

export default SubscribeForm;
